import React from 'react';
import { Store, Globe, Zap, ArrowRight } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';

interface HomePageProps {
  setCurrentPage: (page: string) => void;
}

const HomePage = ({ setCurrentPage }: HomePageProps) => {
  const { trackEvent } = useAnalytics();

  const handleGetStartedClick = () => {
    trackEvent('cta_click', {
      button_name: 'get_started',
      location: 'hero_section'
    });
    setCurrentPage('contact');
  };

  const handleLearnMoreClick = () => {
    trackEvent('cta_click', {
      button_name: 'learn_more',
      location: 'hero_section'
    });
    setCurrentPage('buyers');
  };

  const handleBuyersClick = () => {
    trackEvent('section_click', {
      section_name: 'buyers',
      action: 'view_details'
    });
    setCurrentPage('buyers');
  };

  const handleSellersClick = () => {
    trackEvent('section_click', {
      section_name: 'sellers',
      action: 'view_details'
    });
    setCurrentPage('sellers');
  };

  const handleContactClick = () => {
    trackEvent('cta_click', {
      button_name: 'contact_us',
      location: 'bottom_section'
    });
    setCurrentPage('contact');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Connecting Buyers and Sellers in the Beverage Industry
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Streamline your beverage procurement and distribution with our innovative platform. 
              Access verified partners, optimize your supply chain, and grow your business with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStartedClick}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Get Started
              </button>
              <button
                onClick={handleLearnMoreClick}
                className="bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Buyers Section */}
      <div className="relative bg-white py-24">
        <div className="absolute inset-0 transform -skew-y-6 bg-gray-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                For Beverage Buyers
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Access a network of verified suppliers and streamline your procurement process. 
                Reduce costs, improve quality, and build lasting partnerships.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Store className="h-6 w-6 text-blue-600 mr-2 mt-1" />
                  <span>Find verified suppliers for your specific needs</span>
                </li>
                <li className="flex items-start">
                  <Globe className="h-6 w-6 text-blue-600 mr-2 mt-1" />
                  <span>Access a global network of beverage distributors</span>
                </li>
                <li className="flex items-start">
                  <Zap className="h-6 w-6 text-blue-600 mr-2 mt-1" />
                  <span>Streamline your procurement process</span>
                </li>
              </ul>
              <button
                onClick={handleBuyersClick}
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800"
              >
                Learn more about our buyer solutions
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
            <div className="bg-gray-100 p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">Buyer Benefits</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Reduce procurement costs</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Access verified suppliers</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Streamlined ordering process</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Real-time inventory tracking</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Sellers Section */}
      <div className="relative bg-white py-24">
        <div className="absolute inset-0 transform skew-y-6 bg-gray-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-gray-100 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Seller Benefits</h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Access to verified buyers</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Expand your market reach</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Streamlined order management</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Real-time analytics and insights</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                For Beverage Sellers
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Connect with qualified buyers and grow your distribution network. 
                Our platform helps you reach new markets and streamline your sales process.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Store className="h-6 w-6 text-blue-600 mr-2 mt-1" />
                  <span>Connect with verified buyers in your target market</span>
                </li>
                <li className="flex items-start">
                  <Globe className="h-6 w-6 text-blue-600 mr-2 mt-1" />
                  <span>Expand your distribution network</span>
                </li>
                <li className="flex items-start">
                  <Zap className="h-6 w-6 text-blue-600 mr-2 mt-1" />
                  <span>Streamline your sales process</span>
                </li>
              </ul>
              <button
                onClick={handleSellersClick}
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800"
              >
                Learn more about our seller solutions
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Transform Your Beverage Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join our platform today and connect with the right partners to grow your business.
          </p>
          <button
            onClick={handleContactClick}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 